import { useEffect, useRef } from 'react'

import {
    detachRealtimeEventsClient,
    getRealtimeEventsClient,
} from '../realtime/BrowserRealtimeEventsClient'
import type { RealtimeWorkerOutputMessage } from '../realtime/types'

const useRealtimeEvents = (
    onMessage: (message: RealtimeWorkerOutputMessage) => void,
    enabled = true,
): void => {
    const onMessageRef = useRef(onMessage)

    onMessageRef.current = onMessage

    useEffect(() => {
        if (!enabled) return

        let attachRequest = 0
        let isActive = true
        let removeMessageListener: (() => void) | undefined

        const attach = async () => {
            if (removeMessageListener) return

            const request = ++attachRequest
            const client = await getRealtimeEventsClient()

            if (!isActive || request !== attachRequest) return

            removeMessageListener = client?.addMessageListener((message) =>
                onMessageRef.current(message),
            )
        }

        const detach = () => {
            attachRequest += 1
            removeMessageListener?.()
            removeMessageListener = undefined
        }

        const handlePageHide = (event: PageTransitionEvent) => {
            detach()

            detachRealtimeEventsClient({
                reason: 'pagehide',
                pagePersisted: event.persisted === true,
            })
        }

        const handlePageShow = () => void attach()

        void attach()
        window.addEventListener('pagehide', handlePageHide)
        window.addEventListener('pageshow', handlePageShow)

        return () => {
            window.removeEventListener('pagehide', handlePageHide)
            window.removeEventListener('pageshow', handlePageShow)
            isActive = false
            detach()
        }
    }, [enabled])
}

export default useRealtimeEvents
